import { keyBy } from 'lodash';
import { NormalizedSchema, denormalize, normalize } from 'normalizr';
import type { Team } from 'api/models';
import type {
  DynamicTemplateConditions,
  ProjectTemplate,
  ProjectTemplateApi,
  ProjectTemplateProjectPhase,
  ProjectTemplateSpace,
  ProjectTemplateStoreRecord,
  ProjectTemplateTask,
  TemplateAllocation,
  AllocatedMembers,
  ProjectTemplateSprint,
  MilestoneRevenueRecognitionConfigs,
} from '../types';
import { templateSchema } from './schema';

export {
  getDynamicConditionsFromPayload,
  setDynamicConditionInPayload,
} from './transform-filters';

export interface NormalizedProjectTemplatePayload {
  template: ProjectTemplate;
  projectPhases: {
    [key: string]: ProjectTemplateProjectPhase;
  };
  tasks: {
    [key: string]: ProjectTemplateTask;
  };
  sprints: {
    [key: string]: ProjectTemplateSprint;
  };
  spaceList: {
    [key: string]: ProjectTemplateSpace;
  };
  dynamicConditions: DynamicTemplateConditions;
  allocations: {
    [key: number]: TemplateAllocation;
  };
  allocatedMembers: AllocatedMembers;
  placeholders: {
    [key: number]: Team;
  };
  milestoneRevenueRecognitionConfigs?: MilestoneRevenueRecognitionConfigs[];
}
export const normalizeTemplatePayload = (
  payload: ProjectTemplateApi,
): NormalizedSchema<NormalizedProjectTemplatePayload, string> =>
  normalize(payload, templateSchema);

export const denormalizePayload = (
  payload: NormalizedProjectTemplatePayload,
): ProjectTemplateApi => {
  const denormalizedData = denormalize(
    { template: payload.template },
    { template: templateSchema },
    payload,
  );

  return denormalizedData.template;
};

export const validatePhaseShiftInPayload = (
  projectTemplate: NormalizedProjectTemplatePayload,
): NormalizedProjectTemplatePayload => {
  const { projectPhases } = projectTemplate;

  const sortedPhases = Object.values(projectPhases).sort(
    (phase1, phase2) => phase1.startDay - phase2.startDay,
  );

  const _phases = sortedPhases.map((_phase, index) => {
    const { startDay, shiftDays } = _phase;

    if (shiftDays && shiftDays > 0) {
      const nextPhase = sortedPhases[index + 1];
      const nextStartDay = nextPhase?.startDay ?? 0;

      const maxShiftDays = Math.max(0, nextStartDay - startDay);

      if (shiftDays > maxShiftDays) {
        return { ..._phase, shiftDays: maxShiftDays };
      }
    }

    return _phase;
  });

  return {
    ...projectTemplate,
    projectPhases: keyBy(_phases, 'projectPhaseId'),
  };
};

export const getDynamicFieldsForPreview = (
  projectTemplate: ProjectTemplateStoreRecord,
) => {
  const { dynamicConditions } = projectTemplate;
  const fields = {};

  if (dynamicConditions) {
    Object.values(dynamicConditions).forEach(({ criteria }) => {
      criteria.forEach(({ field }) => {
        // @ts-expect-error TODO: Temporarily expecting error to enable tsc check, Please fix the type error
        const { fieldId, fieldName } = field;

        fields[fieldId < 0 ? fieldName : fieldId] = field;
      });
    });
  }

  return fields;
};
